import {
	Button,
	VStack,
	Modal,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	useToast,
	Input,
	Heading,
} from '@tempo/core';
import React from 'react';
import {useTranslation} from 'next-i18next';
import Toast, {INTENT} from '@components/Toast';
import {useUpdateGroupSettingsMutation} from '@graphql/generated';
import type {GroupSelected} from '../LeaveGroupModal';

interface ModifyGroupModalProps {
	isOpen: boolean;
	onClose: () => void;
	groupSelected: GroupSelected;
}

const ModifyGroupModal = ({
	isOpen,
	onClose,
	groupSelected,
}: ModifyGroupModalProps) => {
	const {t} = useTranslation();
	const toast = useToast({
		duration: 3000,
	});

	const [newGroupName, setNewGroupName] = React.useState<string>(
		groupSelected.name,
	);

	const [updateGroupName] = useUpdateGroupSettingsMutation({
		variables: {
			input: {
				musicTogetherGroupId: groupSelected.id,
				name: newGroupName,
			},
		},
		update: (cache) => {
			cache.modify({
				id: cache.identify({
					__typename: 'MusicTogetherGroup',
					id: groupSelected,
				}),
				fields: {
					name() {
						return newGroupName;
					},
				},
			});
		},
		onCompleted: ({musicTogetherUpdateGroupSettings}) => {
			onClose();

			if (
				musicTogetherUpdateGroupSettings.__typename ===
				'MusicTogetherUpdateGroupSettingsError'
			) {
				return toast({
					render: () => (
						<Toast intent={INTENT.ERROR}>
							{t('errormessage_text_anerroroccurredpleasetryagain_web')}
						</Toast>
					),
				});
			}

			if (
				musicTogetherUpdateGroupSettings.__typename ===
				'MusicTogetherUpdateGroupSettingsOutput'
			) {
				toast({
					render: () => (
						<Toast intent={INTENT.SUCCESS}>
							{t('musictogethergroupnameedit_text_groupnameupdated_web')}
						</Toast>
					),
				});
			}
		},
		onError: () => {
			onClose();
			return toast({
				render: () => (
					<Toast intent={INTENT.ERROR}>
						{t('errormessage_text_anerroroccurredpleasetryagain_web')}
					</Toast>
				),
			});
		},
	});

	return (
		<Modal onClose={onClose} isOpen={isOpen} isCentered>
			<ModalOverlay />
			<ModalContent mx="spacing.l" textAlign={['left', 'center']}>
				<ModalHeader>
					<Heading
						variant="heading.m"
						color="text.neutral.primary.default"
						mb="spacing.m"
					>
						{t('musictogethergroupnameedit_title_editgroupname_web')}
					</Heading>
				</ModalHeader>
				<ModalBody textAlign="center">
					<Input
						w={['100%', '50%']}
						value={newGroupName}
						name="groupName"
						onChange={(e) => setNewGroupName(e.target.value)}
						maxLength={50}
						fontSize="md"
					/>
				</ModalBody>
				<ModalFooter>
					<VStack spacing="spacing.m">
						<Button
							disabled={newGroupName === ''}
							onClick={() => updateGroupName()}
						>
							{t('generic_action_save_web')}
						</Button>

						<Button onClick={onClose} variant="ghost">
							{t('generic_action_cancel_web')}
						</Button>
					</VStack>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export default ModifyGroupModal;
